import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button/Button'
import {RefreshCw05Icon, XCloseIcon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {AgendaButtonFilterSelection} from '@/features/agenda/components/agenda-button-filter-selection/AgendaButtonFilterSelection'
import {AgendaSidebarCard} from '@/features/agenda/components/agenda-sidebar-card/AgendaSidebarCard'
import {AgendaSidebarCardLoader} from '@/features/agenda/components/agenda-sidebar-card/AgendaSidebarCardLoader'
import {AgendaSubject} from '@/features/agenda/types'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {useMemo, useState} from 'react'
import {InputText} from '@/components/commons/input-text/InputText'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {
    StyledAgendaSidebarViewCardScrollArea,
    StyledAgendaSidebarViewContent
} from '@/features/agenda/components/agenda-sidebar-view-card/style'
import {Divider} from '@/components/ui/divider/Divider'
import {AvailableDatesButton} from '@/features/agenda/components/available-dates-button/AvailableDatesButton'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'
import {useAgendaCategoryPatientAgeRange} from '@/features/agenda/hooks/useAgendaCategoryPatientAgeRange'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {EventNextAvailabilityButton} from '@/features/agenda/components/event-next-availability-button/EventNextAvailabilityButton'

export const AgendaSidebarViewCard = ({
    specializationId,
    specializationName
}: {
    specializationId: string
    specializationName: string
}) => {
    const {t} = useTranslation()
    const [search, setSearch] = useState('')
    const {navigateToView} = useAgendaNavigation()
    const {
        searchParams: {specializationIds, category, subjectIds},
        isSubjectSelected,
        setSearchParams,
        clearSearchParams
    } = useAgendaSearchParams()

    const {users, isLoading, isError, isRefetching, refetch} = useAgendaCategoryPatientAgeRange()

    const handleClickTitle = () => {
        setSearchParams({
            patientId: undefined,
            category: category,
            specializationIds: specializationIds,
            subjectIds: undefined
        })
    }

    const handleClickOperator = (operator: AgendaSubject) => {
        setSearchParams({
            patientId: undefined,
            category: category,
            specializationIds: specializationIds,
            subjectIds: subjectIds?.includes(operator.id)
                ? subjectIds.filter(id => id != operator.id)
                : [...(subjectIds || []), operator.id]
        })
    }

    const handleRemoveView = () => {
        navigateToView('week')
        clearSearchParams()
    }

    const filteredUsers = useMemo(() => {
        if (!users) return []
        return users.filter(operator => operator.fullName.toLowerCase().includes(search.toLowerCase()))
    }, [users, search])

    if (isLoading) {
        return <AgendaSidebarCardLoader title={specializationName} />
    }

    if (isError) {
        return (
            <AgendaSidebarCard title={specializationName}>
                <InputHelpText error={t('errors:default')} />
                <Button disabled={isRefetching} variant="ghost" size="xs" shape="text" onClick={() => refetch()}>
                    {isRefetching ? <Spinner /> : <RefreshCw05Icon />}
                    {t('commons:retry')}
                </Button>
            </AgendaSidebarCard>
        )
    }

    return (
        <AgendaSidebarCard
            title={specializationName}
            isSelected={specializationIds?.includes(specializationId)}
            onClickTitle={handleClickTitle}
            onRemove={handleRemoveView}
        >
            <InputText
                placeholder={t('commons:search_placeholder')}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
            />
            <StyledAgendaSidebarViewCardScrollArea scrollbar={<Scrollbar />}>
                <StyledAgendaSidebarViewContent gap={1} direction="column" fullWidth>
                    {filteredUsers.length ? (
                        filteredUsers.map(operator => (
                            <AgendaButtonFilterSelection
                                key={operator.id}
                                onClick={() => handleClickOperator(operator)}
                                isSelected={
                                    isSubjectSelected(operator.id) &&
                                    !!(subjectIds?.length && subjectIds?.length < (users?.length ?? 0))
                                }
                            >
                                <Avatar name={operator.fullName} size={'xs'} />
                                <Flexbox gap={1} align="center" fullWidth>
                                    <p style={{width: '100%'}}>{operator.fullName}</p>
                                    {isSubjectSelected(operator.id) && subjectIds?.length !== users?.length && (
                                        <XCloseIcon />
                                    )}
                                </Flexbox>
                            </AgendaButtonFilterSelection>
                        ))
                    ) : (
                        <Text size="textSm" palette="neutral" shade="500">
                            {t('commons:no_results')}
                        </Text>
                    )}
                </StyledAgendaSidebarViewContent>
            </StyledAgendaSidebarViewCardScrollArea>
            {subjectIds?.length && subjectIds.length > 1 ? (
                <>
                    <Divider direction="horizontal" />
                    <AvailableDatesButton />
                </>
            ) : null}
            {users.length ? (
                <>
                    <Divider direction="horizontal" />
                    <EventNextAvailabilityButton usersIds={subjectIds || users.map(user => user.id)} />
                </>
            ) : null}
        </AgendaSidebarCard>
    )
}
